import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useForm, Controller, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v4'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { useCurrentHousehold } from '@/hooks/useCurrentHousehold'
import { useHousehold } from '@/hooks/useHousehold'
import { useSampleHousehold } from '@/hooks/useSampleHousehold'
import { renameHousehold } from '@/api/households'
import { CURRENCY_OPTIONS, type CurrencyCode } from '@/lib/format'
import { ROUTES } from '@/routes/routes'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Sheet } from '@/components/ui/sheet'
import { MembersManager } from './MembersManager'
import { SettingsPanel, SettingsRow, ROW_BUTTON, DANGER_BUTTON } from './SettingsPanel'
import { cn } from '@/lib/utils'

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Household name is required')
    .max(60, 'Household name must be 60 characters or fewer'),
  currency: z.string(),
  financialYearStartMonth: z.coerce.number(),
})
type FormValues = {
  name: string
  currency: CurrencyCode
  financialYearStartMonth: 1 | 4
}

function peopleCount(users: number, tracked: number) {
  return `${users} ${users === 1 ? 'user' : 'users'} · ${tracked} tracked`
}

const FY_LABEL: Record<1 | 4, string> = {
  4: 'April–March (India)',
  1: 'January–December',
}

export function HouseholdPanel() {
  const { data: household, isLoading } = useHousehold()
  const { role, isSample, switchHousehold } = useCurrentHousehold()
  const { sample, startCreate, startDelete, dialogs: sampleDialogs } = useSampleHousehold()
  const navigate = useNavigate()
  const { hash, key } = useLocation()
  const [editing, setEditing] = useState(false)
  // A link to #members (onboarding, Income tab) opens the list, even when Settings is already
  // open; a tap on Manage/Hide wins until the next navigation.
  const [toggled, setToggled] = useState<{ key: string; open: boolean } | null>(null)
  const showMembers = toggled?.key === key ? toggled.open : hash === '#members'

  const isOwner = role === 'OWNER'
  const isArchived = !!household?.archivedAt
  const canEdit = isOwner && !isArchived
  const fy = (household?.financialYearStartMonth ?? 4) as 1 | 4
  const currencyLabel =
    CURRENCY_OPTIONS.find((c) => c.code === household?.currency)?.label ?? household?.currency

  return (
    <SettingsPanel
      id="household"
      title="Household"
      overlays={
        <>
          {sampleDialogs}
          {editing && household && (
            <EditHouseholdSheet
              initial={{
                name: household.name,
                currency: household.currency,
                financialYearStartMonth: fy,
              }}
              onClose={() => setEditing(false)}
            />
          )}
        </>
      }
      aside={
        isArchived && (
          <span className="rounded-full bg-[var(--fl-warning-bg)] px-2.5 py-0.5 font-medium text-[var(--fl-warning-text)]">
            Archived — read-only
          </span>
        )
      }
    >
      {isLoading ? (
        <p className="py-4 text-sm text-[var(--fl-text-3)]">Loading…</p>
      ) : (
        <>
          <SettingsRow label="Name" value={household?.name} />
          <SettingsRow label="Currency" value={currencyLabel} />
          <SettingsRow
            label="Financial year"
            hint="How Analytics and reports group a year. Your data doesn't change."
            value={FY_LABEL[fy]}
          />
          {canEdit && (
            <div className="flex justify-end py-2.5">
              <Button variant="outline" className={ROW_BUTTON} onClick={() => setEditing(true)}>
                Edit household
              </Button>
            </div>
          )}

          <div id="members" className="scroll-mt-4">
            <SettingsRow
              label="Members & people"
              hint={peopleCount(household?.userCount ?? 0, household?.trackedPeopleCount ?? 0)}
            >
              <Button
                variant="outline"
                className={ROW_BUTTON}
                aria-expanded={showMembers}
                onClick={() => setToggled({ key, open: !showMembers })}
              >
                {showMembers ? 'Hide' : 'Manage'}
              </Button>
            </SettingsRow>
            {showMembers && (
              <div className="pb-4">
                <MembersManager />
              </div>
            )}
          </div>

          <SampleDataRow
            isSample={isSample}
            hasSample={!!sample}
            onOpen={() => {
              if (!sample) return
              switchHousehold(sample.id)
              navigate(ROUTES.APP.DASHBOARD)
            }}
            onCreate={startCreate}
            onDelete={startDelete}
          />
        </>
      )}
    </SettingsPanel>
  )
}

function SampleDataRow({
  isSample,
  hasSample,
  onOpen,
  onCreate,
  onDelete,
}: {
  isSample: boolean
  hasSample: boolean
  onOpen: () => void
  onCreate: () => void
  onDelete: () => void
}) {
  if (isSample) {
    return (
      <SettingsRow label="Sample data" hint="You're looking at sample data.">
        <Button variant="outline" className={cn(ROW_BUTTON, DANGER_BUTTON)} onClick={onDelete}>
          Delete sample household
        </Button>
      </SettingsRow>
    )
  }
  if (hasSample) {
    return (
      <SettingsRow
        label="Sample data"
        hint="The Sharma family sample household is ready to look around."
      >
        <Button variant="outline" className={ROW_BUTTON} onClick={onOpen}>
          Open
        </Button>
        <Button variant="outline" className={cn(ROW_BUTTON, DANGER_BUTTON)} onClick={onDelete}>
          Delete
        </Button>
      </SettingsRow>
    )
  }
  return (
    <SettingsRow
      label="Sample data"
      hint="See how everything looks with a made-up household. Your own data isn't touched."
    >
      <Button variant="outline" className={ROW_BUTTON} onClick={onCreate}>
        Explore with sample data
      </Button>
    </SettingsRow>
  )
}

function EditHouseholdSheet({ initial, onClose }: { initial: FormValues; onClose: () => void }) {
  const queryClient = useQueryClient()
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema) as Resolver<FormValues>,
    defaultValues: initial,
  })

  async function onSubmit(values: FormValues) {
    try {
      await renameHousehold({
        name: values.name,
        currency: values.currency,
        financialYearStartMonth: Number(values.financialYearStartMonth) as 1 | 4,
      })
      await queryClient.invalidateQueries({ queryKey: ['household'] })
      await queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      toast.success('Household settings updated.')
      onClose()
    } catch {
      toast.error('Failed to update household settings. Please try again.')
    }
  }

  return (
    <Sheet title="Household" onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-1">
          <label htmlFor="householdName" className="text-sm font-medium text-[var(--fl-text-1)]">
            Household name
          </label>
          <Input id="householdName" {...register('name')} maxLength={60} />
          {errors.name && <p className="text-xs text-[var(--fl-error)]">{errors.name.message}</p>}
        </div>

        <div className="space-y-1">
          <p className="text-sm font-medium text-[var(--fl-text-1)]">Currency</p>
          <Controller
            control={control}
            name="currency"
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                aria-label="Currency"
              >
                {CURRENCY_OPTIONS.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </Select>
            )}
          />
        </div>

        <div className="space-y-1">
          <p className="text-sm font-medium text-[var(--fl-text-1)]">Financial year</p>
          <Controller
            control={control}
            name="financialYearStartMonth"
            render={({ field }) => (
              <Select
                value={String(field.value ?? 4)}
                onChange={(v) => field.onChange(Number(v))}
                onBlur={field.onBlur}
                aria-label="Financial year"
              >
                <option value="4">{FY_LABEL[4]}</option>
                <option value="1">{FY_LABEL[1]}</option>
              </Select>
            )}
          />
          <p className="text-xs text-[var(--fl-text-2)]">
            How Analytics and reports group a year. Your data doesn't change.
          </p>
        </div>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || !isDirty}>
            {isSubmitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Sheet>
  )
}
